import type { RunEventResponse, ScenarioStateResponse } from "@/api/types";

/**
 * frontier-vanguard 클라 사이드 엔딩 궤적 추정기.
 *
 * <p>서버 {@code FrontierEndingEvaluator.evaluate()} 규칙을 완화한 근사 버전.
 * 실제 엔딩은 런 종료 시 서버가 확정하지만, 플레이어가 "지금 내가 어느 방향으로
 * 가고 있는지" 알게 하려고 라이브 상태로 궤적 태그 한 줄을 보여준다.
 *
 * <p>사용자 결정 (2026-04-22 A.2 = B 궤적 태그): 확률 % 는 노출하지 않고
 * "정복에 기울어짐" 같은 문장 톤으로 방향만 암시한다. 메타게임 유도 방지 +
 * 판타지 톤 유지.
 *
 * <p>5 엔딩 미러:
 * - frontier-conqueror (정복)
 * - border-mediator (화의)
 * - returned-vanguard (수호의 귀환)
 * - hollow-return (공허한 귀환)
 * - buried-beyond-line (선 너머에 묻힘)
 */

export type FrontierEndingTrajectoryId =
    | "conquest"
    | "armistice"
    | "guardian-return"
    | "hollow-return"
    | "buried"
    | "open";

export interface FrontierEndingTrajectory {
    id: FrontierEndingTrajectoryId;
    /** 플레이어 친화 라벨. 서사 톤. */
    label: string;
    /** 1~2줄 부연. hover/펼침에 노출. */
    detail: string;
    /** 톤 컬러 토큰 키 (참조만, 렌더링 시 실제 토큰에서 가져옴). */
    tone: "ember" | "moss" | "steel" | "burgundy" | "parchment";
}

export interface FrontierTrajectoryShiftNotice {
    title: string;
    message: string;
    tone: "info" | "success" | "warning" | "error";
    presentation: "banner" | "flash";
}

export function summarizeFrontierTrajectoryFootprints(
    eventLog: Array<Pick<RunEventResponse, "sourceType" | "title" | "message">>,
    maxItems = 2,
): string[] {
    const relevant = [...eventLog]
        .reverse()
        .filter((event) => (event.sourceType ?? "").includes("scenario"))
        .slice(0, 6);

    const traces: string[] = [];
    const push = (value: string) => {
        if (value && !traces.includes(value)) traces.push(value);
    };

    for (const event of relevant) {
        const haystack = `${event.title ?? ""} ${event.message ?? ""}`;
        if (includesAny(haystack, ["화의", "약조", "협상", "중재", "설득"])) {
            push("최근 화의와 약조 쪽 결단이 이어져, 이번 주도 협상과 수습의 흐름이 잘 이어집니다");
            continue;
        }
        if (includesAny(haystack, ["정복", "돌격", "장악", "강행", "밀어붙"])) {
            push("최근 공세와 장악 쪽 결단이 이어져, 이번 주도 강하게 밀어붙이는 선택이 더 자연스럽습니다");
            continue;
        }
        if (includesAny(haystack, ["귀환", "귀환선", "지켜", "방어", "깃발", "엄호"])) {
            push("최근 귀환선과 수호 쪽 판단이 이어져, 이번 주도 버티고 지키는 쪽의 무게가 큽니다");
            continue;
        }
        if (includesAny(haystack, ["추모", "편지", "불의 밤", "이름", "장례"])) {
            push("최근 추모와 기억의 장면이 이어져, 이번 주 판단도 그 여운과 후속 장면을 의식하게 됩니다");
            continue;
        }
        if (includesAny(haystack, ["보급", "장부", "부채", "청구", "끼니", "배급"])) {
            push("최근 보급과 빚의 문제가 커져, 이번 주는 회복보다 정비와 수습을 먼저 보는 편이 안전합니다");
            continue;
        }
        if (includesAny(haystack, ["유적", "봉인", "정찰", "징후", "능선", "길"])) {
            push("최근 징후와 길을 읽는 선택이 이어져, 이번 주도 정찰과 우회 쪽 판단이 잘 맞습니다");
            continue;
        }
        if (event.title) {
            push(`${event.title}의 여운이 남아 있어, 이번 주 선택도 그 뒤를 잇는 쪽이 더 자연스럽습니다`);
        }
    }

    return traces.slice(0, maxItems);
}

const TRAJECTORIES: Record<FrontierEndingTrajectoryId, FrontierEndingTrajectory> = {
    conquest: {
        id: "conquest",
        label: "정복에 기울어짐",
        detail: "전선의 깃발이 앞을 향한다. 점령으로 선을 다시 긋는 결말에 가까워지고 있다.",
        tone: "ember",
    },
    armistice: {
        id: "armistice",
        label: "화의에 기울어짐",
        detail: "칼보다 장부가 먼저 쥐어질 준비가 되어 있다. 협상으로 선을 다시 묶는 방향이다.",
        tone: "steel",
    },
    "guardian-return": {
        id: "guardian-return",
        label: "수호의 귀환에 기울어짐",
        detail: "이름을 잃으면서도 지켜낸 자리가 많다. 함께 돌아오는 결말로 흐르고 있다.",
        tone: "moss",
    },
    "hollow-return": {
        id: "hollow-return",
        label: "공허한 귀환에 기울어짐",
        detail: "몸은 돌아오겠지만, 변경의 마음은 닫히고 있다. 승리 없는 복귀에 가깝다.",
        tone: "parchment",
    },
    buried: {
        id: "buried",
        label: "선 너머에 묻힐 위험",
        detail: "상처와 추모가 함께 쌓였다. 선을 넘지 못한 채 끝날 가능성이 커졌다.",
        tone: "burgundy",
    },
    open: {
        id: "open",
        label: "아직 방향이 굳지 않았다",
        detail: "어느 결로도 아직 선명하게 흐르고 있지 않다. 다음 몇 주의 선택이 크게 갈릴 수 있다.",
        tone: "parchment",
    },
};

/**
 * 현재 scenarioState + outcomeTags 에서 가장 가까운 궤적 1개를 뽑는다.
 *
 * 우선순위 (서버 평가 규칙 순):
 * 1. tags 에 "conquest" + protector 낮음 → conquest
 * 2. tags 에 "brokered-peace" + bound-company + 보급 정상 → armistice
 * 3. tags 에 "living-return" + memorialWeight>=3 + protector>=1 → guardian-return
 * 4. tags 에 "living-return" + frontierTrust<-10 → hollow-return
 * 5. expeditionWear>=70 + memorialWeight>=4 + scarred + !brokered → buried
 * 6. tags 에 brokered-peace → armistice (fallback)
 * 7. tags 에 conquest → conquest (fallback)
 * 8. 균형 기반: morale+trust 양수면 guardian-return, 음수면 hollow-return
 * 9. 아직 굳지 않음 → open
 */
export function projectFrontierTrajectory(
    state: ScenarioStateResponse | null | undefined,
): FrontierEndingTrajectory {
    if (!state) return TRAJECTORIES.open;
    const tags = state.outcomeTags ?? [];

    const hasTag = (t: string) => tags.includes(t);
    const tagScore = (t: string) => tags.filter(x => x === t).length;

    if (hasTag("conquest") && !hasTag("brokered-peace") && tagScore("protector") < 2) {
        return TRAJECTORIES.conquest;
    }
    if (hasTag("brokered-peace") && hasTag("bound-company") && state.supplyLineState !== "collapsed") {
        return TRAJECTORIES.armistice;
    }
    if (hasTag("living-return") && state.memorialWeight >= 3 && tagScore("protector") >= 1) {
        return TRAJECTORIES["guardian-return"];
    }
    if (hasTag("living-return") && !hasTag("mourned") && state.frontierTrust < -10) {
        return TRAJECTORIES["hollow-return"];
    }
    if (state.expeditionWear >= 70 && state.memorialWeight >= 4
        && hasTag("scarred") && !hasTag("brokered-peace")) {
        return TRAJECTORIES.buried;
    }

    // Fallback tier — 태그가 부분적으로만 찍힌 중기 상태
    if (hasTag("brokered-peace")) return TRAJECTORIES.armistice;
    if (hasTag("conquest")) return TRAJECTORIES.conquest;
    if (hasTag("rear-overrun")
        || (state.expeditionWear >= 70 && hasTag("scarred"))) {
        return TRAJECTORIES.buried;
    }

    // 상태 기반 러프 추정 — 초중반
    const alignment = state.troopMorale + state.frontierTrust;
    if (alignment >= 16) return TRAJECTORIES["guardian-return"];
    if (alignment <= -16) return TRAJECTORIES["hollow-return"];
    if (state.memorialWeight >= 5 && state.expeditionWear >= 50) return TRAJECTORIES.buried;

    return TRAJECTORIES.open;
}

export function detectFrontierTrajectoryShift(
    previous: ScenarioStateResponse | null | undefined,
    next: ScenarioStateResponse | null | undefined,
): FrontierTrajectoryShiftNotice | null {
    const before = projectFrontierTrajectory(previous);
    const after = projectFrontierTrajectory(next);
    if (before.id === after.id) return null;

    const tone = noticeToneForTrajectory(after.id);
    return {
        title: `${after.label}`,
        message: shiftMessage(before.id, after.id),
        tone,
        presentation: after.id === "buried" ? "flash" : "banner",
    };
}

function noticeToneForTrajectory(id: FrontierEndingTrajectoryId): "info" | "success" | "warning" | "error" {
    switch (id) {
        case "guardian-return":
        case "armistice":
            return "success";
        case "conquest":
            return "warning";
        case "buried":
            return "error";
        case "hollow-return":
            return "warning";
        case "open":
        default:
            return "info";
    }
}

function shiftMessage(
    before: FrontierEndingTrajectoryId,
    after: FrontierEndingTrajectoryId,
): string {
    if (after === "conquest") {
        return before === "open"
            ? "이제 원정은 협상보다 장악 쪽으로 더 분명히 기울기 시작합니다."
            : "최근 결단이 전열과 공세를 더 앞세우며 정복의 결을 짙게 만들었습니다.";
    }
    if (after === "armistice") {
        return "장부와 약조를 붙드는 선택이 쌓이며, 이번 원정은 화의의 결을 더 강하게 띠기 시작합니다.";
    }
    if (after === "guardian-return") {
        return "지키며 돌아오려는 선택이 누적되어, 귀환선이 다시 또렷한 길처럼 보이기 시작합니다.";
    }
    if (after === "hollow-return") {
        return "몸은 돌아와도 마음은 비어 갈 수 있다는 징후가 커졌습니다. 지금의 균형이 서늘하게 기웁니다.";
    }
    if (after === "buried") {
        return "상처와 추모가 한 선을 넘었습니다. 이제 이 원정은 끝내 선 너머에 묻힐 위험이 크게 다가옵니다.";
    }
    return "아직 어느 결말도 완전히 굳지 않았습니다. 다음 몇 번의 선택이 방향을 다시 세울 수 있습니다.";
}

function includesAny(text: string, needles: string[]): boolean {
    return needles.some((needle) => text.includes(needle));
}
